import React from 'react';
import { StyleSheet, Text, TextProps } from 'react-native';
import type { IconBaseProps, IconType } from 'react-icons';
import {
  MdAccountBalanceWallet,
  MdAccountBalance,
  MdAdd,
  MdApps,
  MdBuild,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdCheckCircle,
  MdDone,
  MdClose,
  MdCreditCard,
  MdDeliveryDining,
  MdDirectionsCar,
  MdEvent,
  MdExplore,
  MdHourglassEmpty,
  MdMoreHoriz,
  MdInsertChart,
  MdDonutLarge,
  MdPsychology,
  MdRadioButtonChecked,
  MdRadioButtonUnchecked,
  MdPublic,
  MdGroup,
  MdFavorite,
  MdFavoriteBorder,
  MdFitnessCenter,
  MdFlight,
  MdForum,
  MdChatBubbleOutline,
  MdChatBubble,
  MdHome,
  MdKeyboardArrowLeft,
  MdLock,
  MdLocationOn,
  MdPersonAdd,
  MdReceipt,
  MdPieChart,
  MdFileDownload,
  MdCloudUpload,
  MdRefresh,
  MdRestaurant,
  MdCheck,
  MdLandscape,
  MdPhotoCamera,
  MdShoppingBag,
  MdStore,
  MdTune,
  MdVisibility,
  MdDescription,
  MdExpandLess,
  MdExpandMore,
  MdStar,
  MdErrorOutline,
  
} from 'react-icons/md';

interface IconProps extends TextProps {
  name: string;
  size?: number;
  color?: string;
}

const toElement = (Icon: IconType) => {
  return (props: IconBaseProps) => Icon(props) as React.JSX.Element;
};

const ICON_MAP: Record<string, (props: IconBaseProps) => React.JSX.Element> = {
  lock: toElement(MdLock),
  explore: toElement(MdExplore),
  'person-add': toElement(MdPersonAdd),
  'check-box': toElement(MdCheckBox),
  'check-box-outline-blank': toElement(MdCheckBoxOutlineBlank),
  'account-balance-wallet': toElement(MdAccountBalanceWallet),
  add: toElement(MdAdd),
  apps: toElement(MdApps),
  build: toElement(MdBuild),
  'check-circle': toElement(MdCheckCircle),
  close: toElement(MdClose),
  'credit-card': toElement(MdCreditCard),
  'delivery-dining': toElement(MdDeliveryDining),
  'directions-car': toElement(MdDirectionsCar),
  event: toElement(MdEvent),
  favorite: toElement(MdFavorite),
  'favorite-border': toElement(MdFavoriteBorder),
  'fitness-center': toElement(MdFitnessCenter),
  flight: toElement(MdFlight),
  forum: toElement(MdForum),
  home: toElement(MdHome),
  'keyboard-arrow-left': toElement(MdKeyboardArrowLeft),
  'location-on': toElement(MdLocationOn),
  'more-horiz': toElement(MdMoreHoriz),
  receipt: toElement(MdReceipt),
  refresh: toElement(MdRefresh),
  restaurant: toElement(MdRestaurant),
  'shopping-bag': toElement(MdShoppingBag),
  store: toElement(MdStore),
  done: toElement(MdDone),
  check: toElement(MdDone),
  tune: toElement(MdTune),
  visibility: toElement(MdVisibility),
  psychology: toElement(MdPsychology),
  'hourglass-empty': toElement(MdHourglassEmpty),
  museum: toElement(MdAccountBalance),
  landscape: toElement(MdLandscape),
  'photo-camera': toElement(MdPhotoCamera),
  'account-balance': toElement(MdAccountBalance),
  'radio-button-checked': toElement(MdRadioButtonChecked),
  'radio-button-unchecked': toElement(MdRadioButtonUnchecked),
  public: toElement(MdPublic),
  people: toElement(MdGroup),
  group: toElement(MdGroup),
  description: toElement(MdDescription),
  'expand-less': toElement(MdExpandLess),
  'expand-more': toElement(MdExpandMore),
  star: toElement(MdStar),
  'error-outline': toElement(MdErrorOutline),
  // file and cloud upload icons
  'file-download': toElement(MdFileDownload),
  'file_download': toElement(MdFileDownload),
  'cloud-upload': toElement(MdCloudUpload),
  'cloud_upload': toElement(MdCloudUpload),
  insert_chart: toElement(MdInsertChart),
  'insert-chart': toElement(MdInsertChart),
  donut_large: toElement(MdDonutLarge),
  'donut-large': toElement(MdDonutLarge),
  pie_chart: toElement(MdPieChart),
  'pie-chart': toElement(MdPieChart),
  // chat / forum mappings
  'chat_bubble_outline': toElement(MdChatBubbleOutline),
  'chat-bubble-outline': toElement(MdChatBubbleOutline),
  'chat_bubble': toElement(MdChatBubble),
  'chat-bubble': toElement(MdChatBubble),
  chat: toElement(MdChatBubble),
};

const MaterialIcons: React.FC<IconProps> = ({ name, size = 16, color = '#000', style, ...rest }) => {
  // Avoid forwarding pointerEvents as a DOM prop (react-native-web deprecation).
  // Extract pointerEvents from rest and merge into style instead.
  const { pointerEvents, ...restProps } = rest as any;
  const IconComponent = ICON_MAP[name];
  if (!IconComponent) {
    return (
      <Text style={[styles.fallback(size, color), style, pointerEvents ? ({ pointerEvents } as any) : null]} {...restProps}>
        {name}
      </Text>
    );
  }

  const flattened = StyleSheet.flatten(style) ?? {};
  const finalSize = (flattened as any).fontSize ?? size;
  const finalColor = (flattened as any).color ?? color;

  const mergedStyle = pointerEvents ? { ...(flattened as any), pointerEvents } : flattened as any;

  return <IconComponent size={finalSize} color={finalColor} style={mergedStyle} />;
};

const styles = {
  fallback: (fontSize: number, fallbackColor: string) => ({
    fontSize,
    color: fallbackColor,
    fontFamily: 'sans-serif',
  }),
};

export default MaterialIcons;
